import React, { useState, useEffect } from 'react';
import './HoroscopeList.css';
import AddHoroscopeForm from './AddHoroscopeForm';
import apiClient from '../../api';

const HoroscopeList = () => {
  const [horoscopes, setHoroscopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingHoroscope, setEditingHoroscope] = useState(null);
  const [editFormData, setEditFormData] = useState({
    sign: '', timespan: '', prediction: '', luckyNumbers: '', luckyColors: ''
  });

  useEffect(() => {
    fetchHoroscopes();
  }, []);

  const fetchHoroscopes = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/rashi/horoscope/admin/');
      const data = response.data;
      if (Array.isArray(data)) {
        setHoroscopes(data);
      } else if (data && Array.isArray(data.results)) {
        setHoroscopes(data.results);
      } else {
        setHoroscopes([]);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching horoscopes:', error);
      setError('Failed to load horoscopes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (horoscopeId) => {
    if (!window.confirm('Are you sure you want to delete this horoscope?')) return;

    try {
      await apiClient.delete(`/api/rashi/horoscope/${horoscopeId}/`);
      alert('Horoscope deleted successfully');
      await fetchHoroscopes();
    } catch (error) {
      console.error('Error deleting horoscope:', error);
      alert('Error deleting horoscope');
    }
  };

  const handleSave = async (formData) => {
    try {
      await apiClient.post('/api/rashi/horoscope/admin/', formData);
      setShowAddForm(false);
      alert('Horoscope created successfully');
      await fetchHoroscopes();
    } catch (error) {
      console.error('Error creating horoscope:', error);
      alert('Error creating horoscope');
    }
  };

  const handleEditClick = (horoscope) => {
    setEditingHoroscope(horoscope);
    setEditFormData({
      sign: horoscope.sign || '',
      timespan: horoscope.timespan || '',
      prediction: horoscope.prediction || '',
      luckyNumbers: horoscope.luckyNumbers || '',
      luckyColors: horoscope.luckyColors || ''
    });
  };

  const handleEditCancel = () => {
    setEditingHoroscope(null);
    setEditFormData({ sign: '', timespan: '', prediction: '', luckyNumbers: '', luckyColors: '' });
  };

  const handleEditSave = async () => {
    try {
      await apiClient.put(`/api/rashi/horoscope/${editingHoroscope.id}/update/`, editFormData);
      alert('Horoscope updated successfully');
      setEditingHoroscope(null);
      setEditFormData({ sign: '', timespan: '', prediction: '', luckyNumbers: '', luckyColors: '' });
      await fetchHoroscopes();
    } catch (error) {
      console.error('Error updating horoscope:', error);
      alert('Error updating horoscope');
    }
  };

  const getZodiacEmoji = (sign) => {
    const emojiMap = {
      aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
      leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
      sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓'
    };
    return emojiMap[sign] || '⭐';
  };

  const getTimespanIcon = (timespan) => {
    const iconMap = {
      today: '📅', tomorrow: '🔮', weekly: '📆', monthly: '🗓️', yearly: '📊'
    };
    return iconMap[timespan] || '📅';
  };

  const filteredHoroscopes = horoscopes.filter(horoscope =>
    horoscope.sign.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (horoscope.timespan && horoscope.timespan.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (horoscope.prediction && horoscope.prediction.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (showAddForm) {
    return <AddHoroscopeForm onClose={() => setShowAddForm(false)} onSave={handleSave} />;
  }

  if (loading) return <div className="loading-message">Loading horoscopes...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const zodiacSigns = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];
  const timespans = ['today', 'tomorrow', 'weekly', 'monthly', 'yearly'];

  return (
    <div className="horoscope-list">
      {/* Edit Modal */}
      {editingHoroscope && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <h2>✏️ Edit Horoscope</h2>
            <div className="edit-form">
              <label>Zodiac Sign:</label>
              <select
                value={editFormData.sign}
                onChange={(e) => setEditFormData({ ...editFormData, sign: e.target.value })}
              >
                {zodiacSigns.map(sign => (
                  <option key={sign} value={sign}>{getZodiacEmoji(sign)} {sign.charAt(0).toUpperCase() + sign.slice(1)}</option>
                ))}
              </select>
              <label>Timespan:</label>
              <select
                value={editFormData.timespan}
                onChange={(e) => setEditFormData({ ...editFormData, timespan: e.target.value })}
              >
                {timespans.map(ts => (
                  <option key={ts} value={ts}>{getTimespanIcon(ts)} {ts.charAt(0).toUpperCase() + ts.slice(1)}</option>
                ))}
              </select>
              <label>Prediction:</label>
              <textarea
                value={editFormData.prediction}
                onChange={(e) => setEditFormData({ ...editFormData, prediction: e.target.value })}
                rows={6}
              />
              <label>Lucky Numbers:</label>
              <input
                type="text"
                value={editFormData.luckyNumbers}
                onChange={(e) => setEditFormData({ ...editFormData, luckyNumbers: e.target.value })}
                placeholder="e.g., 3, 7, 12"
              />
              <label>Lucky Colors:</label>
              <input
                type="text"
                value={editFormData.luckyColors}
                onChange={(e) => setEditFormData({ ...editFormData, luckyColors: e.target.value })}
                placeholder="e.g., Red, Blue, Green"
              />
              <div className="edit-modal-buttons">
                <button className="save-button" onClick={handleEditSave}>💾 Save</button>
                <button className="cancel-button" onClick={handleEditCancel}>❌ Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="horoscope-header">
        <h1 className="horoscope-title">🔮 Horoscope Management</h1>
        <button onClick={() => setShowAddForm(true)} className="add-horoscope-button">
          ➕ Add New Horoscope
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="🔍 Search horoscopes by zodiac sign, timespan, or prediction..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredHoroscopes.length === 0 && horoscopes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔮</div>
          <h3 className="empty-title">No Horoscopes Found</h3>
          <p className="empty-description">Start creating horoscope predictions for different zodiac signs.</p>
          <button onClick={() => setShowAddForm(true)} className="add-horoscope-button">
            ➕ Create Your First Horoscope
          </button>
        </div>
      ) : filteredHoroscopes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3 className="empty-title">No Matching Horoscopes Found</h3>
          <p className="empty-description">Try adjusting your search term.</p>
        </div>
      ) : (
        <div className="horoscope-grid">
          {filteredHoroscopes.map((horoscope) => (
            <div key={horoscope.id} className="horoscope-card">
              <div className="horoscope-header-info">
                <div className="zodiac-info">
                  <span className="zodiac-emoji">{getZodiacEmoji(horoscope.sign)}</span>
                  <div className="zodiac-details">
                    <h3>{horoscope.sign}</h3>
                  </div>
                </div>
                <span className="timespan-badge">
                  {getTimespanIcon(horoscope.timespan)} {horoscope.timespan}
                </span>
              </div>

              <div className="horoscope-prediction">{horoscope.prediction}</div>

              {(horoscope.luckyNumbers || horoscope.luckyColors) && (
                <div className="lucky-info">
                  {horoscope.luckyNumbers && (
                    <div className="lucky-item">
                      <span>🔢</span>
                      <div><strong>Lucky Numbers:</strong><br />{horoscope.luckyNumbers}</div>
                    </div>
                  )}
                  {horoscope.luckyColors && (
                    <div className="lucky-item">
                      <span>🎨</span>
                      <div><strong>Lucky Colors:</strong><br />{horoscope.luckyColors}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="horoscope-actions">
                <button onClick={() => handleEditClick(horoscope)} className="edit-button">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDelete(horoscope.id)} className="delete-button">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HoroscopeList;